// src/components/usage/usage-tables.tsx
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import CustomTable from "components/shared/table";
import { Alert } from "components/ui/alert";
import { useIdentity } from "contexts/identity-context";
import { AppTotal } from "contexts/models";
import { UsageTableProps } from "./types";
import { resolveAppName } from "./utils-usage";

import { cols, UsageRow } from "./usage-columns";
import { modifyColumns, UsageCreditByTimeRow } from "./usage-utils";

const parseDateString = (dateString: string) => {
  const [day, month, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

export const UsageTable = (props: UsageTableProps) => {
  const [identityState] = useIdentity();

  const usageQuery = props.usageQuery;
  const apiKeysQuery = props.apiKeysQuery;

  return (
    <div className="flex flex-col w-full mb-8">
      <ReactQueryErrorWrapper
        queries={[usageQuery, apiKeysQuery]}
        mutations={[]}
      />

      {props.isSuccess && (
        <CustomTable
          rows={buildRowData(apiKeysQuery, usageQuery)}
          columns={cols}
          isLoading={props.isLoading}
          overlayText={"No usage data"}
          exportFileName="usage-credits-by-app.csv"
        />
      )}
    </div>
  );
};

export const UsageCreditByTimeTable = (props: UsageTableProps) => {
  const usageQuery = props.usageQuery;

  function getRowId(row: UsageCreditByTimeRow) {
    return String(row.Date);
  }

  return (
    <div className="flex flex-col w-full">
      {props.isError && (
        <Alert variant="destructive">
          An error occurred while fetching the usage data.
        </Alert>
      )}

      {props.isSuccess && (
        <>
          <CustomTable
            rows={usageQuery.data?.appCreditsData.rows}
            columns={modifyColumns(
              usageQuery.data?.appCreditsData.columns,
              usageQuery.data?.appCreditsData.group,
            )}
            isLoading={props.isLoading}
            getRowId={getRowId}
            overlayText={"No usage data"}
            exportFileName="usage-credits-by-time.csv"
          />
        </>
      )}
    </div>
  );
};

const totalOrZero = (v: AppTotal | undefined) => {
  if (v !== undefined) {
    return Number(v.totalCredits.toFixed(1));
  }
  return 0;
};

interface AppTotalUsageEntry {
  product: string;
  totalCredits: number;
}

interface AppProductTotal {
  name: string;
  totalUsage: AppTotalUsageEntry[];
}

const buildRowData = (
  apiKeysQuery: { data: { name: string; displayName?: string }[] },
  usageQuery: { data?: { appProductTotals: AppProductTotal[] } },
): UsageRow[] => {
  let keyNameMapping: Record<string, string> = {};
  apiKeysQuery.data.forEach(function (key: {
    name: string;
    displayName?: string;
  }) {
    const keyName: string = key.name;
    const keyDisplayName: string = key.displayName ? key.displayName : key.name;
    keyNameMapping[keyName] = keyDisplayName;
  });

  const rows: UsageRow[] = [];

  usageQuery.data?.appProductTotals.forEach((app: AppProductTotal) => {
    if (app.totalUsage.length === 0 && keyNameMapping[app.name] === undefined) {
      return;
    }

    rows.push({
      id: app.name,
      keyName: resolveAppName(app.name, keyNameMapping),
      predictive: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("predictive"),
        ),
      ),
      addresses: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("addresses"),
        ),
      ),
      landparcels: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("land-parcels"),
        ),
      ),
      buildings: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("buildings"),
        ),
      ),
      batches: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("batches"),
        ),
      ),
      adminbounds: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("administrative-boundaries"),
        ),
      ),
      maps: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("maps"),
        ),
      ),
      esri: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("esri-compatible-locator"),
        ),
      ),
      clip: totalOrZero(
        app.totalUsage.find((x: AppTotalUsageEntry) =>
          x.product.includes("clip"),
        ),
      ),
    });
  });

  return rows;
};
